import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { Field, Formik } from 'formik';
import * as Yup from 'yup';
import { v4 as uuidv4 } from 'uuid';

import {
  Text,
  Box,
  Button,
  Checkbox,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Textarea,
  VStack,
  ModalFooter,
  Input,
  Icon,
  useColorModeValue,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { MdFileDownload } from 'react-icons/md';

import { useDispatch, useSelector } from 'react-redux';

import { updateFileSubmissionSession } from 'store/modules/subquest/subquest-action';
import { handleDownloadFile, extractFileName } from 'utils/fileHelper';

import FormikFormControl from 'components/common/forms/FormikFormControl';
import ConfirmationActionModal from 'components/common/modals/ConfirmationActionModal';
import ResultModal from 'components/common/modals/ResultModal';
import { ReducerStatus } from 'utils/constants';
import { subquestActions } from 'store/modules/subquest/subquest-slice';
import NumberInput from 'components/common/forms/NumberInput';
import { resultTextColors, resultText } from 'utils/constants/result';

function UpdateFileSubmissionSessionMd({ classId }) {
  const dispatch = useDispatch();

  const [pendingFormValues, setPendingFormValues] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [formInputs, setFormInputs] = useState({
    scoreType: '',
    totalScore: 100,
    passingScore: 85,
    score: 0,
    remarks: '',
    showRemarks: false,
  });

  const subQuest = useSelector((state) => state.subquest.subQuest);
  const getSubQuestStatus = useSelector(
    (state) => state.subquest.getSubQuest.status
  );

  const selectedFileSubmissionSession = useSelector(
    (state) => state.subquest.fileSubmissionSession
  );
  const updateFileSubmissionSessionState = useSelector(
    (state) => state.subquest.updateFileSubmissionSession
  );

  const baseFileUrl = process.env.REACT_APP_FILE_URL;
  const bgColor = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    if (subQuest && updateFileSubmissionSessionState.modal) {
      setTimeout(() => {
        setFormInputs({
          scoreType: subQuest?.subQuestFileSubmission?.scoreType || '',
          totalScore: subQuest?.subQuestFileSubmission?.totalScore || 100,
          passingScore: subQuest?.subQuestFileSubmission?.passingScore || 85,
          score: selectedFileSubmissionSession?.score || 0,
          remarks: selectedFileSubmissionSession?.remarks || '',
          showRemarks: selectedFileSubmissionSession?.showRemarks || false,
        });
      }, 300);
    }
  }, [
    subQuest,
    updateFileSubmissionSessionState.modal,
    selectedFileSubmissionSession,
  ]);

  const formikForm = useRef();

  const formValidation = Yup.object({
    scoreType: Yup.string(),
    topScore: Yup.number(),
    score: Yup.number().min(0).max(100).required('Required'),
    remarks: Yup.string(),
    showRemarks: Yup.boolean(),
  });

  const handleToggleModal = () => {
    dispatch(subquestActions.setUpdateFileSubmissionSession({ modal: false }));
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
    setPendingFormValues(null);
  };

  const handleConfirmationResult = () => {
    setShowConfirmation(false);
  };

  const handleResult = () => {
    dispatch(
      subquestActions.setUpdateFileSubmissionSession({
        status: ReducerStatus.IDLE,
      })
    );

    if (updateFileSubmissionSessionState.status === ReducerStatus.SUCCEEDED) {
      setFormInputs({
        scoreType: '',
        totalScore: 100,
        passingScore: 85,
        score: 0,
        remarks: '',
        showRemarks: false,
      });

      dispatch(
        subquestActions.setUpdateFileSubmissionSession({
          modal: false,
          status: ReducerStatus.IDLE,
          error: null,
        })
      );
    }
  };

  const handleSubmitForm = (values) => {
    setPendingFormValues(values);
    setShowConfirmation(true);
  };

  const handleTriggerSubmit = () => {
    formikForm?.current?.submitForm();
  };

  const handleConfirmSubmission = () => {
    if (!pendingFormValues) return;

    setFormInputs({ ...pendingFormValues });

    const result =
      pendingFormValues.score >= formInputs.passingScore
        ? resultText.Pass
        : resultText.Fail;

    const sessionData = {
      classId,
      score: pendingFormValues.score,
      result,
      status: 'Graded',
      remarks: pendingFormValues.remarks,
      showRemarks: pendingFormValues.showRemarks,
    };

    dispatch(
      updateFileSubmissionSession({
        id: selectedFileSubmissionSession.fileSubmissionSessionId,
        sessionData,
      })
    );

    setShowConfirmation(false);
    setPendingFormValues(null);
  };

  const getResultTextColor = (result) => {
    return resultTextColors[result] || resultTextColors.Default;
  };

  const getConfirmationDescription = () => {
    if (!pendingFormValues) return '';

    const result =
      pendingFormValues.score >= formInputs.passingScore
        ? resultText.Pass
        : resultText.Fail;

    return (
      <>
        <Text mb={3}>
          Are you sure you want to submit the grade for this file submission?
        </Text>
        <VStack align="start" spacing={1}>
          <Text>Sub-Quest Name: {subQuest?.name || 'N/A'}</Text>
          <Text>
            Score: {pendingFormValues?.score || 0}/
            {formInputs?.totalScore || 100}
          </Text>
          <Text>
            Result:{' '}
            <Text
              as="span"
              fontWeight="bold"
              color={getResultTextColor(result)}
            >
              {result}
            </Text>
          </Text>
        </VStack>
      </>
    );
  };

  const renderSubmittedFiles = () => {
    const filePaths = selectedFileSubmissionSession?.filePaths || [];

    if (!filePaths || filePaths.length === 0) {
      return (
        <Box
          p={3}
          bg={bgColor}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="md"
          textAlign="center"
        >
          <Text fontSize="sm" color="gray.500">
            No files submitted
          </Text>
        </Box>
      );
    }

    return (
      <VStack spacing={2} align="stretch">
        {filePaths.map((filePath, index) => {
          const fileName = extractFileName(
            filePath,
            `Submitted_File_${index + 1}.zip`
          );

          return (
            <Box
              key={uuidv4()}
              p={3}
              bg={bgColor}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="md"
              transition="background-color 0.2s"
            >
              <HStack spacing={3}>
                <Box fontSize="lg">📁</Box>
                <VStack align="start" spacing={0} flex={1}>
                  <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                    {fileName}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    ZIP Archive
                  </Text>
                </VStack>
                <Icon
                  as={MdFileDownload}
                  color="blue.500"
                  boxSize={5}
                  cursor="pointer"
                  onClick={() =>
                    handleDownloadFile(baseFileUrl, filePath, fileName)
                  }
                />
              </HStack>
            </Box>
          );
        })}
      </VStack>
    );
  };

  return (
    <Box>
      <ConfirmationActionModal
        isVisible={showConfirmation}
        onClose={handleCancelConfirmation}
        onConfirm={handleConfirmSubmission}
        onSubmitResult={handleConfirmationResult}
        status={updateFileSubmissionSessionState.status}
        successMessage="The file submission sub-quest has been graded successfully."
        error={updateFileSubmissionSessionState.error}
      >
        {getConfirmationDescription()}
      </ConfirmationActionModal>

      {updateFileSubmissionSessionState.status === ReducerStatus.SUCCEEDED ||
      updateFileSubmissionSessionState.status === ReducerStatus.FAILED ? (
        <ResultModal
          isOpen={updateFileSubmissionSessionState.modal}
          onSubmit={handleResult}
          isError={
            updateFileSubmissionSessionState.status === ReducerStatus.FAILED
          }
          title={
            updateFileSubmissionSessionState.status === ReducerStatus.SUCCEEDED
              ? 'File Submission Graded'
              : null
          }
          message={
            updateFileSubmissionSessionState.status === ReducerStatus.SUCCEEDED
              ? 'The file submission has been graded successfully.'
              : updateFileSubmissionSessionState.error?.data?.title ||
                'Server Error'
          }
          errorObject={updateFileSubmissionSessionState.error?.data?.errors}
        />
      ) : (
        <Modal
          size="3xl"
          closeOnOverlayClick={
            updateFileSubmissionSessionState.status !== ReducerStatus.LOADING
          }
          isOpen={updateFileSubmissionSessionState.modal}
          onClose={handleToggleModal}
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Grade File Submission</ModalHeader>
            <ModalCloseButton />
            {getSubQuestStatus === ReducerStatus.LOADING ? (
              <Center>
                <Spinner
                  thickness="4px"
                  speed="0.65s"
                  emptyColor="gray.200"
                  color="blue.500"
                  size="xl"
                />
              </Center>
            ) : (
              <ModalBody my={3}>
                <Formik
                  enableReinitialize
                  innerRef={formikForm}
                  initialValues={formInputs}
                  onSubmit={(values) => {
                    handleSubmitForm(values);
                  }}
                  validationSchema={formValidation}
                >
                  {({ values, setFieldValue }) => (
                    <Box>
                      <VStack spacing={3} align="stretch">
                        <HStack spacing={4} align="flex-start">
                          <FormikFormControl
                            label="Score Type"
                            helperText="The defined score setting for the file submission."
                            flex="2"
                          >
                            <Input
                              value={formInputs.scoreType || ''}
                              isReadOnly
                            />
                          </FormikFormControl>

                          <FormikFormControl
                            label="Total Score"
                            helperText="The maximum score for this sub-quest."
                            flex="1"
                          >
                            <Input
                              value={formInputs.totalScore || ''}
                              isReadOnly
                            />
                          </FormikFormControl>

                          <FormikFormControl
                            label="Passing Score"
                            helperText="The minimum score to pass this sub-quest."
                            flex="1"
                          >
                            <Input
                              value={formInputs.passingScore || ''}
                              isReadOnly
                            />
                          </FormikFormControl>
                        </HStack>

                        <Field name="score">
                          {({ field, form }) => (
                            <NumberInput
                              label="Score"
                              helperText="Enter the score for this file submission."
                              w="50%"
                              errors={form.errors.score}
                              touched={form.touched.score}
                              field={field}
                              max={values?.totalScore}
                              value={values.score}
                              onChange={(value) =>
                                setFieldValue('score', parseInt(value, 10) || 0)
                              }
                            />
                          )}
                        </Field>

                        <Field name="remarks">
                          {({ field, form }) => (
                            <FormikFormControl
                              label="Remarks"
                              helperText="Provide any remarks or feedback for the file submission."
                              errors={form.errors.remarks}
                              touched={form.touched.remarks}
                            >
                              <Textarea {...field} />
                            </FormikFormControl>
                          )}
                        </Field>

                        <Field name="showRemarks">
                          {({ field, form }) => (
                            <FormikFormControl
                              label=""
                              helperText=""
                              errors={form.errors.showRemarks}
                              touched={form.touched.showRemarks}
                            >
                              <Checkbox
                                size="md"
                                isChecked={field.value}
                                onChange={field.onChange}
                                name={field.name}
                              >
                                Show Remarks
                              </Checkbox>
                              <Text fontSize="sm" color="gray.500">
                                If checked, the remarks will be visible to the
                                trainee.
                              </Text>
                            </FormikFormControl>
                          )}
                        </Field>
                      </VStack>
                    </Box>
                  )}
                </Formik>

                {/* Submitted Files Section */}
                <Box mt={6}>
                  <Text fontSize="md" fontWeight="semibold" mb={3}>
                    Submitted Files (
                    {selectedFileSubmissionSession?.filePaths?.length || 0})
                  </Text>
                  {renderSubmittedFiles()}
                </Box>
              </ModalBody>
            )}

            <ModalFooter>
              <Button
                variant="outline"
                colorScheme="blue"
                mr={3}
                onClick={handleToggleModal}
                isDisabled={
                  updateFileSubmissionSessionState.status ===
                  ReducerStatus.LOADING
                }
              >
                Close
              </Button>
              <Button
                colorScheme="red"
                px={10}
                onClick={handleTriggerSubmit}
                isLoading={
                  updateFileSubmissionSessionState.status ===
                  ReducerStatus.LOADING
                }
              >
                Submit
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
}

UpdateFileSubmissionSessionMd.defaultProps = {
  classId: null,
};

UpdateFileSubmissionSessionMd.propTypes = {
  classId: PropTypes.number,
};

export default UpdateFileSubmissionSessionMd;
